import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton, DatePicker } from 'antd';
import moment from 'moment';
import { Button } from 'antd-v5';
import { notificationSuccess, notificationError } from '../../../../core/actions';
import {
  getMerchantLifeEventTypes,
  getClientLifeEvents,
  createClientLifeEvent,
  updateClientLifeEvent,
  deleteClientLifeEvent,
} from '../../../../services/clients';
import { transformLifeEventsResponse, transformLifeEventResponse } from '../../../../services/clientTransformers';
import { Buttons } from './Buttons';
import PopConfirm from '../../PopConfirm';
import RadioButton from '../../RadioButton';
import '../infoDrawer.css';

// Merges merchant-configured life event types with the client's existing events.
// Typed rows appear first (one per merchant type, pre-populated with the client's
// date when they have one, or empty when they don't). Untyped rows follow.
function buildMergedList(merchantTypes, clientEvents) {
  const typedMap = new Map(clientEvents.filter(e => e.life_event_type_id != null).map(e => [e.life_event_type_id, e]));
  const typedRows = merchantTypes.map(t => {
    const existing = typedMap.get(t.id);
    return existing ? { ...existing } : { id: null, type: t.name, life_event_type_id: t.id, date: '' };
  });
  const untypedRows = clientEvents.filter(e => e.life_event_type_id == null);
  return [...typedRows, ...untypedRows];
}

const LifeEvents = ({ merchantId, client, auth }) => {
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  const clientId = client.client_id;
  const [timeFrame, setTimeFrame] = useState('repeats');
  const [newDate, setNewDate] = useState(null);
  const [name, setName] = useState('');
  const [newLifeEvent, setNewLifeEvent] = useState(false);
  const [selectedLE, setSelectedLE] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [merchantTypes, setMerchantTypes] = useState([]);
  // savedLifeEvents is the last-persisted merged list; localLifeEvents is the working copy
  const [savedLifeEvents, setSavedLifeEvents] = useState([]);
  const [localLifeEvents, setLocalLifeEvents] = useState([]);
  // Track which indices in localLifeEvents have been modified by the user
  const [changedIndices, setChangedIndices] = useState(new Set());

  const lifeEvents = localLifeEvents;

  useEffect(() => {
    let canceled = false;

    if (merchantId && clientId) {
      setLoading(true);
      (async () => {
        try {
          const [typesData, eventsData] = await Promise.all([
            getMerchantLifeEventTypes({ loadingStatusOff: true }),
            getClientLifeEvents({ clientId, loadingStatusOff: true }),
          ]);
          if (!canceled) {
            const types = Array.isArray(typesData?.results) ? typesData.results : [];
            const events = transformLifeEventsResponse(eventsData);
            const merged = buildMergedList(types, events);
            setMerchantTypes(types);
            setSavedLifeEvents(merged);
            setLocalLifeEvents(merged);
          }
        } finally {
          if (!canceled) setLoading(false);
        }
      })();
    }

    return () => {
      canceled = true;
    };
  }, [merchantId, clientId]);

  const LifeEventData = () => {
    const returnArr = [];
    if (loading) {
      returnArr.push(
        <div key="skeleton">
          <Skeleton
            paragraph={{
              rows: 3,
            }}
            active
          />
        </div>,
      );
      return returnArr;
    }
    if (lifeEvents.length > 0) {
      lifeEvents.forEach(event => {
        // ask chris tomorrow about life event dates, it looks like typed dates are longer than 4
        if (event.date && `${event.date}`.length === 4) {
          returnArr.push(
            <div className="flex-col-left m-btm-8" key={event.date + event.type}>
              <span className="fs-12 fw-500 gray">{event.type}</span>
              <span className="fs-12 fw-500 black">{moment(event.date, 'MMDD').format('MMMM DD')}</span>
            </div>,
          );
        }
        if (event.date && `${event.date}`.length === 8) {
          returnArr.push(
            <div className="flex-col-left m-btm-8" key={event.date + event.type}>
              <span className="fs-12 fw-500 gray">{event.type}</span>
              <span className="fs-12 fw-500 black">{moment(event.date, 'YYYYMMDD').format('MMMM DD')}</span>
            </div>,
          );
        }
      });
      if (returnArr.length > 0) {
        return returnArr;
      }
    }
    return <span className="gray fs-12">No Life Events</span>;
  };

  const handleSave = useCallback(async () => {
    const changedEvents = lifeEvents.filter((_, i) => changedIndices.has(i));
    if (changedEvents.length === 0) {
      setOpenEdit(false);
      return;
    }
    const toCreate = changedEvents.filter(e => !e.id && e.date && e.life_event_type_id != null);
    const toUpdate = changedEvents.filter(e => e.id && e.date);
    const toDelete = changedEvents.filter(e => e.id && !e.date);
    try {
      await Promise.all([
        ...toCreate.map(e =>
          createClientLifeEvent({
            clientId,
            data: { lifeEventTypeId: e.life_event_type_id, name: e.type, date: e.date },
          }),
        ),
        ...toUpdate.map(e => updateClientLifeEvent({ clientId, eventId: e.id, data: { date: e.date } })),
        ...toDelete.map(e => deleteClientLifeEvent({ clientId, eventId: e.id })),
      ]);
      // Refetch to get fresh ids for newly created events
      const freshData = await getClientLifeEvents({ clientId, loadingStatusOff: true });
      const freshMerged = buildMergedList(merchantTypes, transformLifeEventsResponse(freshData));
      setSavedLifeEvents(freshMerged);
      setLocalLifeEvents(freshMerged);
      setChangedIndices(new Set());
      notificationSuccess('Life Events Updated');
    } catch (err) {
      notificationError('Error Updating Life Events');
    } finally {
      setOpenEdit(false);
    }
  }, [clientId, lifeEvents, changedIndices, merchantTypes]);

  const handleCancel = useCallback(() => {
    setOpenEdit(false);
    setLocalLifeEvents(savedLifeEvents);
    setChangedIndices(new Set());
  }, [savedLifeEvents]);

  const handleDateChange = useCallback((index, dateValue) => {
    setLocalLifeEvents(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], date: dateValue };
      return updated;
    });
    setChangedIndices(ids => new Set(ids).add(index));
  }, []);

  const setEditButton = useCallback(value => {
    setOpenEdit(value);
    setNewLifeEvent(false);
  }, []);

  const handleUpdateLifeEvents = useCallback(
    newLifeEvents => {
      const updatedLifeEvents = [...lifeEvents, ...newLifeEvents];
      setSavedLifeEvents(updatedLifeEvents);
      setLocalLifeEvents(updatedLifeEvents);
    },
    [lifeEvents],
  );

  const handleSaveNew = async () => {
    setLoading(true);
    try {
      const dateFormat = timeFrame === 'oneTime' ? 'YYYYMMDD' : 'MMDD';
      const formatedDate = moment(newDate).format(dateFormat);
      const responseData = await createClientLifeEvent({ clientId, data: { name, date: formatedDate } });
      if (responseData.id) {
        handleUpdateLifeEvents([transformLifeEventResponse(responseData)]);
        setNewLifeEvent(false);
        setOpenEdit(false);
        notificationSuccess('Life Event Created');
      }
    } catch (err) {
      notificationError('Error Creating Life Event');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = () => {
    if (!name) {
      notificationError('Please Add Life Event Name');
      return;
    }

    if (!timeFrame) {
      notificationError('Please Add Life Event Time Frame');
      return;
    }

    if (!newDate) {
      notificationError('Please Set Scheduled Date');
      return;
    }

    handleSaveNew();
  };

  const openDeleteModal = useCallback(lifeEvent => {
    setSelectedLE(lifeEvent);
    setOpenDelete(true);
  }, []);

  const handleDeleteLifeEvent = useCallback(async () => {
    try {
      await deleteClientLifeEvent({ clientId, eventId: selectedLE.id });
      notificationSuccess('Life Event Removed');
      const filtered = lifeEvents.filter(lE => lE.id !== selectedLE.id);
      setSavedLifeEvents(filtered);
      setLocalLifeEvents(filtered);
    } catch (err) {
      notificationError('Error Removing Life Event');
    } finally {
      setOpenDelete(false);
      setSelectedLE(null);
    }
  }, [selectedLE, lifeEvents, clientId]);

  return (
    <div style={{ borderTop: '1px #ebebeb solid' }} id="client-details-life-box">
      <div className="flex-row-center" style={{ position: 'relative', gap: '8px' }}>
        <p className="fw-500 gray fs-15 align-left m-btm-5">Life Events</p>
        {openEdit && !newLifeEvent && (
          <FontAwesomeIcon size="1x" icon={['far', 'plus']} className="pointer" onClick={() => setNewLifeEvent(true)} />
        )}
        {newLifeEvent ? null : (
          <Buttons
            open={openEdit}
            setOpen={setEditButton}
            onSave={handleSave}
            onCancel={handleCancel}
            containerStyles={{ top: '12px' }}
            id="client-details-life-buttons"
            parentId="client-details-life-box"
          />
        )}
      </div>
      {newLifeEvent ? (
        <LifeEventNew
          setTimeFrame={setTimeFrame}
          timeFrame={timeFrame}
          loading={loading}
          handleValidate={handleValidate}
          name={name}
          setName={setName}
          setNewLifeEvent={setNewLifeEvent}
          setNewDate={setNewDate}
        />
      ) : openEdit ? (
        <LifeEventForm lifeEvents={lifeEvents} onDateChange={handleDateChange} openDeleteModal={openDeleteModal} />
      ) : (
        LifeEventData()
      )}
      {openDelete && (
        <PopConfirm
          confirm={`Are you sure you want to delete "${selectedLE.type}"?`}
          description="This will permanently delete this life event."
          onCancel={() => setOpenDelete(false)}
          onConfirm={handleDeleteLifeEvent}
        />
      )}
    </div>
  );
};

const LifeEventNew = ({
  timeFrame,
  setTimeFrame,
  loading,
  handleValidate,
  name,
  setName,
  setNewLifeEvent,
  setNewDate,
}) => {
  return (
    <div className="flex-col-center h-100-P">
      <div className="flex-col-left m-btm-8 w-100-P m-top-15 m-btm-15">
        <span className="fs-12 fw-500 gray">Add Life Event</span>
        <input
          className="fs-12 fw-500 gray outline-none w-100-P client-input m-btm-8"
          placeholder="Life Event Name"
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </div>
      <div
        className="align-left fs-12"
        style={{
          width: '100%',
          marginBottom: '32px',
        }}
        onChange={e => setTimeFrame(e.target.value)}
      >
        <div
          style={{
            marginBottom: '14px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <RadioButton id="repeats" name="expiration" value="repeats" defaultChecked={timeFrame === 'repeats'} />

          <label
            htmlFor="repeats"
            style={{
              marginLeft: '16px',
            }}
          >
            Repeat annually
          </label>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <RadioButton id="oneTime" name="expiration" value="oneTime" defaultChecked={timeFrame === 'oneTime'} />

          <label
            htmlFor="oneTime"
            style={{
              marginLeft: '16px',
            }}
          >
            One-time
          </label>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'left',
          width: '100%',
          marginBottom: '48px',
        }}
      >
        <DatePicker
          style={{
            paddingLeft: '0px',
            fontWeight: '500',
            fontSize: '12px',
            width: '270px',
          }}
          format={timeFrame === 'oneTime' ? 'MM-DD-YYYY' : 'MM-DD'}
          onChange={date => setNewDate(date)}
          bordered={false}
          placeholder="Date"
        />
        <span className="client-input" />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', width: '100%' }}>
        <Button
          onClick={() => {
            setName('');
            setNewDate(null);
            setNewLifeEvent(false);
          }}
        >
          Cancel
        </Button>
        <Button type="primary" onClick={() => handleValidate()}>
          Save
        </Button>
      </div>
    </div>
  );
};

const LifeEventForm = ({ lifeEvents, onDateChange, openDeleteModal }) => {
  return lifeEvents.map((event, index) => {
    return (
      <div className="flex-row-spacebetween-start" key={`${event.type}-${index}`}>
        <div className="flex-col-left m-btm-8" key={event.date} style={{ borderBottom: 'solid 1px #818181' }}>
          <span className="fs-12 fw-500 gray">{event.type}</span>
          {event.life_event_type_id ? (
            <DatePicker
              style={{ paddingLeft: '0px', fontWeight: '500', fontSize: '12px', width: '270px' }}
              format="MM-DD"
              onChange={date => {
                const newDate = date ? moment(date).format('MMDD') : '';
                onDateChange(index, newDate);
              }}
              bordered={false}
              placeholder="Event Date"
              defaultValue={event.date ? moment(event.date, 'MMDD') : undefined}
              type="button"
            />
          ) : (
            <div className="flex-row-spacebetween-nowrap" style={{ width: '270px' }}>
              <DatePicker
                style={{ paddingLeft: '0px', fontWeight: '500', fontSize: '12px', width: '115px' }}
                format="MM-DD"
                onChange={date => {
                  const newDate = date ? moment(date).format('MMDD') : '';
                  onDateChange(index, newDate);
                }}
                bordered={false}
                placeholder="Event Date"
                defaultValue={
                  event.date
                    ? `${event.date}`.length > 4
                      ? moment(event.date, 'YYYYMMDD')
                      : moment(event.date, 'MMDD')
                    : ''
                }
                type="button"
              />
              <div style={{ width: '40px', marginTop: '4px' }}>
                <FontAwesomeIcon
                  icon={['far', 'trash-alt']}
                  className="pointer"
                  color="#FF7A7A"
                  onClick={() => openDeleteModal(event)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  });
};

const mapStateToProps = state => ({
  messaging: state.messaging,
});
export default connect(mapStateToProps)(LifeEvents);
